import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Logger, useLocalize } from '@saviynt/common';
import { Button, ModalIsland } from '@saviynt/design-system';
import PropTypes from 'prop-types';

import {
  getCancelApi,
  getCheckInApi,
  getListRowApi,
} from '../../utilities/api/getDataFromApi';
import { msgs } from '../../utilities/messages';

const KIND = {
  CHECKIN: 'checkIn',
  END: 'end',
  CANCEL_SESSION: 'cancelSession',
  CANCEL_REQUEST: 'cancelRequest',
};

function CheckInModal({
  isModalOpen,
  setIsModalOpen,
  row,
  setListRowData,
  kind,
  userName,
  setCheckInModalKind,
  setAlertBannerData,
  isJitForApps,
}) {
  const accountName = row?.accountName;

  // Roles
  const isRole = Boolean(row?.roleKey);
  const roleName = row?.roleDisplayName || row?.roleName;
  const isRoleWithRoleName = isRole && roleName;

  const localize = useLocalize();

  const [isLoading, setIsLoading] = useState(false);

  // Get filter state from Redux
  const { selectedFilters, searchValue } = useSelector(
    (state) =>
      state.landingPageFilters || { selectedFilters: {}, searchValue: '' }
  );
  const { checkedPlatforms, checkedAssets, checkedRoles } = selectedFilters;

  function alertBannerDescription() {
    if (KIND.CHECKIN === kind) {
      if (isRoleWithRoleName) {
        return localize(msgs.pam.checkIn.successBannerRole, { roleName });
      }

      return localize(msgs.pam.checkIn.successBanner);
    }

    if (KIND.END === kind) {
      if (isRoleWithRoleName) {
        return localize(msgs.pam.end.successBannerRole, { roleName });
      }

      return localize(msgs.pam.end.successBanner, { accountName });
    }

    if (KIND.CANCEL_SESSION === kind) {
      if (isRoleWithRoleName) {
        return localize(msgs.pam.cancelSession.successBannerRole, { roleName });
      }

      return localize(msgs.pam.cancelSession.successBanner, { accountName });
    }

    return localize(msgs.pam.end.successBanner);
  }

  const handleCheckIn = () => {
    setIsLoading(true);

    if (
      KIND.CHECKIN === kind ||
      KIND.END === kind ||
      KIND.CANCEL_SESSION === kind
    ) {
      getCheckInApi(row)
        .then((response) => {
          setIsLoading(false);
          setIsModalOpen(false);
          const isSuccess = response.status === 200;

          function getDescription() {
            if (isSuccess) {
              return Promise.resolve(alertBannerDescription());
            }

            return response.json().then((jsonData) => {
              if (jsonData.error === 'SESSION.ALREADY.ENDED') {
                return localize(msgs.pam.checkIn.alreadyBanner);
              }

              if (KIND.CANCEL_SESSION === kind) {
                if (isRoleWithRoleName) {
                  return localize(msgs.pam.cancelSession.failureBannerRole, {
                    roleName,
                  });
                }

                return localize(msgs.pam.cancelSession.failureBanner, {
                  accountName,
                });
              }

              return localize(jsonData.error);
            });
          }

          let colorTheme;
          let title;

          if (isSuccess) {
            if (KIND.CANCEL_SESSION === kind) {
              colorTheme = 'Information';
              title = localize(msgs.pam.alertBanner.sessionCanceledTitle);
            } else {
              colorTheme = 'Success';
              title = localize(msgs.pam.alertBanner.successTitle);
            }
          } else {
            colorTheme = 'Critical';

            if (KIND.CANCEL_SESSION === kind) {
              title = localize(msgs.pam.alertBanner.sessionNotCanceledTitle);
            } else {
              title = localize(msgs.pam.alertBanner.failureTitle);
            }
          }

          getDescription().then((description) => {
            setAlertBannerData((prevState) => [
              ...prevState,
              {
                colorTheme,
                title: localize(title),
                description: localize(description),
              },
            ]);

            setCheckInModalKind('');

            getListRowApi({
              checkedPlatforms,
              checkedAssets,
              checkedRoles,
              searchValue,
              isJitForApps,
            }).then((res) => {
              setListRowData(res);
            });
          });
        })
        .catch((error) => {
          setIsLoading(false);
          setIsModalOpen(false);
          Logger.error(localize(msgs.pam.checkIn.error), error);

          // Show alert banner when API rejects (e.g., network timeout, 500)
          let title;
          let description;

          if (KIND.CANCEL_SESSION === kind) {
            title = localize(msgs.pam.alertBanner.sessionNotCanceledTitle);
            description = isRoleWithRoleName
              ? localize(msgs.pam.cancelSession.failureBannerRole, { roleName })
              : localize(msgs.pam.cancelSession.failureBanner, { accountName });
          } else {
            title = localize(msgs.pam.alertBanner.failureTitle);
            description = localize(msgs.pam.end.failureBanner);
          }

          setAlertBannerData((prevState) => [
            ...prevState,
            { colorTheme: 'Critical', title, description },
          ]);
          setCheckInModalKind('');
        });
    }

    if (KIND.CANCEL_REQUEST === kind) {
      getCancelApi(row.requestAccessKey, userName)
        .then((response) => {
          setIsLoading(false);
          setIsModalOpen(false);
          const isSuccess =
            response?.response?.result?.toLowerCase() === 'successfull';

          function getDescription() {
            if (isSuccess) {
              if (isRoleWithRoleName) {
                return localize(msgs.pam.cancelRequest.successBannerRole, {
                  roleName,
                });
              }

              return localize(msgs.pam.cancelRequest.successBanner, {
                accountName,
              });
            }

            if (isRoleWithRoleName) {
              return localize(msgs.pam.cancelRequest.failureBannerRole, {
                roleName,
              });
            }

            return localize(msgs.pam.cancelRequest.failureBanner, {
              accountName,
            });
          }

          setAlertBannerData((prevState) => [
            ...prevState,
            {
              colorTheme: isSuccess ? 'Information' : 'Critical',
              title: isSuccess
                ? localize(msgs.pam.alertBanner.requestCanceledTitle)
                : localize(msgs.pam.alertBanner.requestNotCanceledTitle),
              description: getDescription(),
            },
          ]);
          setCheckInModalKind('');
          getListRowApi({
            checkedPlatforms,
            checkedAssets,
            checkedRoles,
            searchValue,
            isJitForApps,
          }).then((res) => {
            setListRowData(res);
          });
        })
        .catch((error) => {
          setIsLoading(false);
          setIsModalOpen(false);
          Logger.error(localize(msgs.pam.checkIn.error), error);

          setAlertBannerData((prevState) => [
            ...prevState,
            {
              colorTheme: 'Critical',
              title: localize(msgs.pam.alertBanner.requestNotCanceledTitle),
              description: isRoleWithRoleName
                ? localize(msgs.pam.cancelRequest.failureBannerRole, {
                    roleName,
                  })
                : localize(msgs.pam.cancelRequest.failureBanner, {
                    accountName,
                  }),
            },
          ]);
          setCheckInModalKind('');
        });
    }
  };

  function alertModalDescription() {
    if (KIND.CHECKIN === kind) {
      if (isRole) {
        return localize(msgs.pam.checkIn.modalDescriptionRole);
      }

      return localize(msgs.pam.checkIn.modalDescription);
    }

    if (KIND.END === kind) {
      return localize(msgs.pam.end.modalDescription);
    }

    if (KIND.CANCEL_SESSION === kind) {
      return localize(msgs.pam.cancelSession.modalDescription);
    }

    if (KIND.CANCEL_REQUEST === kind) {
      return localize(msgs.pam.cancelRequest.modalDescription);
    }

    return localize(msgs.pam.end.modalDescription);
  }

  function alertModalTitle() {
    if (KIND.CHECKIN === kind) {
      return localize(msgs.pam.checkIn.modalTitle);
    }

    if (KIND.END === kind) {
      return localize(msgs.pam.end.modalTitle);
    }

    if (KIND.CANCEL_SESSION === kind) {
      return localize(msgs.pam.cancelSession.modalTitle);
    }

    if (KIND.CANCEL_REQUEST === kind) {
      return localize(msgs.pam.cancelRequest.modalTitle);
    }

    return localize(msgs.pam.end.modalTitle);
  }

  return (
    <ModalIsland
      kind='warning'
      isCloseButtonVisible={!isLoading}
      alertTitle={alertModalDescription()}
      onClose={() => setIsModalOpen(false)}
      isOpen={isModalOpen}
      primaryButton={
        <Button
          type='button'
          kind='outlined'
          size='medium'
          isLoading={isLoading}
          loaderText={`${'Attempting to'} ${alertModalTitle()}`}
          onClick={handleCheckIn}>
          {alertModalTitle()}
        </Button>
      }
      secondaryButton={
        <Button
          type='button'
          kind='ghost'
          size='medium'
          isDisabled={isLoading}
          onClick={() => {
            setIsModalOpen(false);
          }}>
          {localize(msgs.pam.common.cancel)}
        </Button>
      }
    />
  );
}

CheckInModal.defaultProps = { isModalOpen: false, isJitForApps: false };

CheckInModal.propTypes = {
  setIsModalOpen: PropTypes.func.isRequired,
  setListRowData: PropTypes.func.isRequired,
  row: PropTypes.shape({
    requestAccessKey: PropTypes.number,
    accountName: PropTypes.string,
    roleKey: PropTypes.string,
    roleName: PropTypes.string,
    roleDisplayName: PropTypes.string,
    endpointDisplayName: PropTypes.string,
    arsRequestKey: PropTypes.number,
  }).isRequired,
  isModalOpen: PropTypes.bool,
  kind: PropTypes.string.isRequired,
  userName: PropTypes.string.isRequired,
  setCheckInModalKind: PropTypes.func.isRequired,
  setAlertBannerData: PropTypes.func.isRequired,
  isJitForApps: PropTypes.bool,
};

export default CheckInModal;
